(ns ycweb.app.dashboard.planning.planoverview
  (:require
   [rum.core :as rum]
   [ycweb.app.admin.wholesale.common :as w.cmn]
   [ycweb.app.dashboard.planning.common :as planning.cmn]
   [ycweb.app.dashboard.planning.orderoverview]
   [ycweb.app.dashboard.planning.ordered]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.csvexport :as csvexport]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as yuc]
   [ycweb.util.date :as ud]
   [ycweb.util.urlstate :as urlstate]))

(defn category-ready-for-planner? [category user-id]
  (boolean
    (and
      (:ready category)
      (some? user-id)
      (= user-id (:planner-user-id category)))))

(rum/defc level-two-readiness-cell [category user-id]
  (let [ready? (category-ready-for-planner? category user-id)]
    [:div
     {:class
      (yuc/bem :planner-matrix__readiness [(if ready? :ready :not-ready)])}
     [:input
      {:type "checkbox"
       :checked ready?
       :read-only true
       :disabled true}]
     [:span.planner-matrix__readiness-label (:cat-two-name category)]]))

(defn planner-owns-categories?
  "A planner owns a root if they are assigned the root itself or any Level 2 under
   it — owning a Level 1 carries its children, as it does on plan creation."
  [planner-categories user-id categories]
  (let [owned (get planner-categories user-id #{})]
    (boolean
      (some owned
        (into
          (set (map :cat-root-id categories))
          (map :cat-two-id categories))))))

(defn owners-by-root
  "user-ids to show a column for, per root. Until anyone has been assigned a
   category the matrix would be empty, so an unused mapping falls back to every
   planner — the same rule plan creation uses."
  [planners planner-categories categories-by-root]
  (let [all-ids (set (map :user_id planners))]
    (into {}
      (map
        (fn [[root categories]]
          [root
           (if (empty? planner-categories)
             all-ids
             (set
               (map :user_id
                 (filter
                   #(planner-owns-categories? planner-categories (:user_id %) categories)
                   planners))))]))
      categories-by-root)))

(defn root-readiness
  "How far a planner has got through a root: :all when every Level 2 under it is
   ready for them, :some when only part are, :none when none are."
  [categories user-id]
  (let [ready (count (filter #(category-ready-for-planner? % user-id) categories))]
    (cond
      (zero? ready) :none
      (= ready (count categories)) :all
      :else :some)))

(rum/defc planner-readiness-cell [planner categories expanded? on-toggle]
  (let [user-id (:user_id planner)]
    [:div
     {:class (yuc/bem :planner-matrix__planner [(root-readiness categories user-id)])
      :on-click on-toggle}
     [:span.planner-matrix__planner-name (:fullname planner)]
     (when expanded?
       [:div.planner-matrix__level-twos
        (map
          (fn [category]
            (rum/with-key
              (level-two-readiness-cell category user-id)
              (:cat-two-id category)))
          categories)])]))

(rum/defc planner-category-matrix < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        open?-atom (rum/cursor process-atom :planner-matrix-open?)
        open? (rum/react open?-atom)
        expanded-atom (rum/cursor process-atom :planner-matrix-expanded)
        expanded (set (rum/react expanded-atom))
        all-planners (sort-by :fullname (vals (cmn/curact process-atom :planners-idx)))
        planner-categories (cmn/curact process-atom :planner-categories)
        level-two-categories (vals (cmn/curact process-atom :level-two-categories-idx))
        categories-by-root
        (into (sorted-map)
          (map (fn [[root categories]] [root (sort-by :cat-two-name categories)]))
          (group-by :cat-root-name level-two-categories))

        owners (owners-by-root all-planners planner-categories categories-by-root)
        planner-by-id (yuc/index-by :user_id all-planners)

        ;; Keyed by [root planner]: a root can have more than one owner, and
        ;; opening one planner's Level 2s shouldn't open the other's.
        toggle-cell
        (fn [root user-id]
          (swap! expanded-atom
            (fn [cells]
              (let [cells (set cells)
                    cell [root user-id]]
                (if (contains? cells cell)
                  (disj cells cell)
                  (conj cells cell))))))]
    [:div.u-margin-bottom-20.u-margin-top-20
     {:class (yuc/bem :accordion [:card])}
     [:h3.accordion__heading
      [:a
       {:on-click #(swap! open?-atom not)
        :class (yuc/bem :accordion__heading-link [(when open? :is-active)])}
       [:span.accordion__heading-text
        (str "Planner Categories (" (count categories-by-root) ")")]
       [:span.accordion__heading-icon-wrapper
        [:i.accordion__heading-icon.js-accordion-icon
         {:class (yuc/bem :icon [:block :default :is-dark (if open? :minus :plus)])}]]]]
     (when open?
       [:div.planner-matrix
        [:table.planner-matrix__table
         [:thead
          [:tr
           (map
             (fn [[root-category categories]]
               [:th.planner-matrix__root
                {:key root-category}
                [:span.planner-matrix__root-name root-category]
                [:span.planner-matrix__root-count (str " (" (count categories) ")")]])
             categories-by-root)]]
         [:tbody
          [:tr
           (map
             (fn [[root-category categories]]
               (let [owning (sort-by :fullname (keep planner-by-id (get owners root-category #{})))]
                 [:td.planner-matrix__cell {:key root-category}
                  (if (empty? owning)
                    ;; Says so outright rather than leaving an empty cell, which reads
                    ;; as a rendering fault rather than a category nobody owns.
                    [:div.planner-matrix__unassigned "Unassigned"]
                    (map
                      (fn [planner]
                        (rum/with-key
                          (planner-readiness-cell planner categories
                            (contains? expanded [root-category (:user_id planner)])
                            #(toggle-cell root-category (:user_id planner)))
                          (:user_id planner)))
                      owning))]))
             categories-by-root)]]]])]))

;; --- EXISTING METHODS & FNS ---

(defmethod grid/action :stockorder.planning/plan [app-atom _]
  (let [table-atom (datatables/table-cursor app-atom :stockorder/oldplans)
        idx-old-plans @(rum/cursor table-atom :data)
        selected-plan-atom (rum/cursor-in app-atom [:stockorder.planning/dashboard :selected-plan])
        {:keys [planid]} (cmn/screen-params app-atom)]
    (planning.cmn/validate-plan-items app-atom)
    (reset! selected-plan-atom (get idx-old-plans planid))))

(defn supplier-row-classes [row]
  (->>
    [(when (or (planning.cmn/moq-unmet? row) (planning.cmn/mov-unmet? row)) "tablesorter-errorRow")
     (when (= "Ordered" (:supplier-status row)) "plan-ordered")
     (when (yuc/as-bool (:supplier-deleted row)) "plan-deleted")]
    (remove nil?)
    (seq)))

(defmethod grid/action :stockorder.planning.supplierplan/update-status [app-atom {:keys [sps-id]} _]
  (comms/send :stockorder.planning.supplierplan/update-status
    {:stockplanid (:planid (cmn/screen-params app-atom))
     :supplierplanid sps-id}))

(defmethod comms/receive :stockorder.planning.supplierplan/update-status
  [app-atom {:keys [success status sps-id msg]}]
  (cond
    (not success)
    (toaster/open-toast app-atom :error (or msg "Could not change the supplier's status.") {})

    :else
    (do
      (swap! (rum/cursor-in (cmn/process-cursor app-atom) [:suppliers])
        (fn [suppliers]
          (mapv #(if (= (:sps_id %) sps-id) (assoc % :status status) %) suppliers)))
      (when (= status "Ready")
        (swap! (rum/cursor-in (cmn/process-cursor app-atom) [:grouped-entity-flags])
          dissoc ["stock_plan_supplier" sps-id]))
      (planning.cmn/setup-overview-atom app-atom))))

(defn supplier-closed-notice [row]
  (let [now (ud/php-timestamp)
        {:keys [closedstart closedend]} row]
    (cond
      (or (not (yuc/presence closedstart)) (not (yuc/presence closedend)))
      nil

      (and (< now closedend) (> now closedstart))
      (str "Closed until " (ud/format-date (ud/from-php-date closedend) "dd MMM yyyy"))

      (and (< now closedend) (> (+ now (* 86400 31)) closedstart))
      (let [days (max 1 (quot (- closedend closedstart) 86400))]
        (str "Closing soon: On "
          (ud/format-date (ud/from-php-date closedstart) "dd MMM yyyy")
          " (for " days (if (> days 1) " Days)" " Day)"))))))

(def plan-overview-table-fns
  {:config-fns
   {:row-error-fn (fn [_ row] (supplier-row-classes row))}

   :column-fns
   {:supplier-name-fn
    (fn [app-atom _ _ cell-val]
      (let [process-atom (cmn/process-cursor app-atom)
            suppliers-atom (rum/cursor-in process-atom [:suppliers])
            suppliers-by-id (yuc/index-by :id (rum/react suppliers-atom))]
        (get-in suppliers-by-id [cell-val :companyname])))

    ;; The same dot as the SKU table's Ready Status column, at the same size.
    :supplier-ready-toggle
    (fn [app-atom row _ _]
      (let [flags
            (get (cmn/curact (cmn/process-cursor app-atom) :grouped-entity-flags)
              ["stock_plan_supplier" (:sps-id row)])
            ordered? (= "Ordered" (:supplier-status row))]
        [:a
         {:class (yuc/bem :admin-status-dot [(planning.cmn/readiness-dot-modifier (planning.cmn/supplier-readiness row)) :l])
          :title (planning.cmn/supplier-status-title row flags)
          :href "#"
          :on-click
          (fn [e]
            (.preventDefault e)
            (when-not ordered?
              (grid/dispatch-action app-atom :stockorder.planning.supplierplan/update-status
                {:sps-id (:sps-id row)})))}]))

    :supplier-name-with-link
    (fn [app-atom row col cell-val]
      (let [flags
            (get (cmn/curact (cmn/process-cursor app-atom) :grouped-entity-flags)
              ["stock_plan_supplier" (:sps-id row)])

            closed-notice (supplier-closed-notice row)]
        [:div.item-name-status
         [:a
          (urlstate/href-props app-atom "planning-new" "category"
            {:supplierid (:supplier_id row)
             :planid (:planid (cmn/screen-params app-atom))})
          cell-val]
         (when (or (planning.cmn/moq-unmet? row) (planning.cmn/mov-unmet? row))
           [:i
            {:class (yuc/bem :icon [:warning :inline])
             :title "Minimum order value or quantity not met"}])
         (for [flag flags]
           [:div.plan-supplier-warning {:key (:id flag)} (:reason flag)])
         (when closed-notice
           [:div.plan-supplier-warning closed-notice])]))

    :moq-bolding
    (fn [_ row _ cell-val]
      (if (planning.cmn/moq-unmet? row) [:b cell-val] cell-val))

    :mov-bolding
    (fn [_ row _ cell-val]
      (if (planning.cmn/mov-unmet? row) [:b cell-val] cell-val))

    :issue-fn
    (fn [_ row _ _]
      (let [mov? (planning.cmn/mov-unmet? row)
            moq? (planning.cmn/moq-unmet? row)]
        (cond
          (and mov? moq?) "MOV and MOQ unmet"
          mov? "MOV"
          moq? "MOQ"
          :else "None")))

    :needed-cell-class
    (fn [row _] (when (pos? (or (:needed row) 0)) ["plan-warning"]))

    :store-need-cell-class
    (fn [row _] (when (pos? (or (:store-need row) 0)) ["plan-warning-light"]))

    :moq-cell-class
    (fn [row _] (when (planning.cmn/moq-unmet? row) ["plan-warning"]))

    :mov-cell-class
    (fn [row _] (when (planning.cmn/mov-unmet? row) ["plan-warning"]))

    :category-name-with-link
    (fn [app-atom row col cell-val]
      [:a
       (urlstate/href-props app-atom "planning-new" "category"
         {:cat1 (:cat1 row) :cat2 cell-val
          :planid (:planid (cmn/screen-params app-atom))})
       cell-val])

    :category-list
    (fn [_ _ _ cell-val]
      (into [:div] (map (fn [category] [:div category]) cell-val)))

    :supplier-list
    (fn [_ _ _ cell-val]
      (into [:div] (map (fn [supplier] [:div supplier]) cell-val)))}

   :footer
   (let [sum-fn (fn [_ _ cell-vals] (reduce + 0 (remove nil? cell-vals)))]
     {:needed sum-fn
      :store-need sum-fn
      :brands sum-fn
      :variations sum-fn
      :qty sum-fn
      :minorderqty (fn [_ _ cell-vals] (planning.cmn/moq-total cell-vals))
      :minordervalue sum-fn
      :rsp
      (fn [_ _ cell-vals]
        (yuc/currency-format (reduce + 0 (remove nil? cell-vals)) 2))
      :cost
      (fn [_ _ cell-vals]
        ;; :cost is in cents and currency-format already divides by 100 — a
        ;; second division here put the total out by a factor of 100.
        (yuc/currency-format (reduce + 0 (remove nil? cell-vals)) 2))})})

(def supplier-overview-table-config
  {:table-name :stockorder.planning/supplier-overview
   :row-id :supplier_id
   :row-class-k :row-error-fn
   :data-source {:cursor [:admin.planning-new :suppliers-overview]}
   :column-defs
   [{:key :supplier-status :title "Ready Status"
     :align-class :u-text-center
     :render {:render-k :supplier-ready-toggle}}
    {:key :supplier_id :title "Supplier"
     :render
     {:logic-k :supplier-name-fn
      :render-k :supplier-name-with-link}}
    {:key :avgdeliverydays :title "Lead Time"}
    {:key :plannername :title "Planner"}
    {:key :needed :title "Needed for Customers"
     :sorting-key :total-needed
     :cell-classes-k :needed-cell-class}
    {:key :store-need :title "Needed for Stores"
     :cell-classes-k :store-need-cell-class}
    {:key :orders-placed :title "Orders Placed"}
    {:key :brands :title "Brands"}
    {:key :variations :title "Variations"}
    {:key :qty :title "Units"
     :cell-classes-k :moq-cell-class}
    {:key :minorderqty :title "Min Order Qty"
     :cell-classes-k :moq-cell-class
     :render {:render-k :moq-bolding}}
    {:key :cost :title "Cost" :type :tocurrency
     :cell-classes-k :mov-cell-class}
    {:key :rsp :title "Total RSP" :type :tocurrency}
    {:key :minordervalue :title "Min Order Value"
     :type :currency
     :cell-classes-k :mov-cell-class
     :render {:render-k :mov-bolding}}
    {:key :subcategories :title "Subcategories"}
    {:key :categories :title "Categories"
     :render {:render-k :category-list}}
    {:key :supplier_id :title "Issues"
     :render {:logic-k :issue-fn}}]})

(def category-overview-table-config
  {:table-name :admin.planning-new/category-overview
   :row-id :cat2
   :data-source {:cursor [:admin.planning-new :suppliers-overview]}
   :column-defs
   [{:key :cat2 :title "Level 2 Category"
     :render {:render-k :category-name-with-link}}
    {:key :needed :title "Needed for Customers"}
    {:key :store-need :title "Needed for Stores"}
    {:key :orders-placed :title "Orders Placed"}
    {:key :brands :title "Brands"}
    {:key :subcategories :title "Subcategories"}
    {:key :variations :title "Variations"}
    {:key :suppliers-mov-moq :title "Suppliers with MOV / MOQ"}
    {:key :category-suppliers :title "Suppliers"
     :render {:render-k :supplier-list}}
    {:key :cost :title "Cost" :type :tocurrency}
    {:key :rsp :title "Total RSP" :type :tocurrency}]})

(defn plan-cost-summary
  "Reads back what the last run actually produced, against what was asked for.
   Every amount arrives in cents."
  [{:keys [totalcost budget fillrate solvedfillrate]}]
  (let [budget (or budget 0)
        totalcost (or totalcost 0)]
    (str
      "Plan cost R" (yuc/currency-format totalcost 2)
      (when (pos? budget) (str " of R" (yuc/currency-format budget 2) " budget"))
      (cond
        (and (pos? budget) (> totalcost budget))
        ;; No fillrate reaches this budget: minimum stock levels and store needs
        ;; survive a forecast of zero, so they set a floor above it.
        " — over budget. Minimum stock levels and store requirements alone cost more than this budget, so no fillrate can meet it."

        (and
          (pos? (or solvedfillrate 0))
          (pos? (or fillrate 0))
          (< solvedfillrate fillrate))
        (str " — budget capped the fillrate at " solvedfillrate "%, below the " fillrate "% requested")))))

(defn plan-basis-note
  "The budget is solved against the forecast, but the plan is what survives its
   Filter and the availability rules. Those are different baskets and the gap is
   large, so saying only the plan cost makes the budget look ignored."
  [{:keys [totalcost forecastcost budget]}]
  (let [totalcost (or totalcost 0)
        forecastcost (or forecastcost 0)]
    (when (and (pos? (or budget 0)) (> forecastcost totalcost))
      (str
        "Budget is solved against the forecast, which came to R"
        (yuc/currency-format forecastcost 2) ". R"
        (yuc/currency-format (- forecastcost totalcost) 2)
        " of that is then dropped by this plan's Filter and availability rules, so the"
        " plan costs less than the budget it was solved for."))))

(rum/defc plan-parameters < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        summary (cmn/curact process-atom [:plan-summary])
        ordered-count (or (cmn/curact process-atom [:plan-ordered-count]) 0)
        params-atom (rum/cursor-in process-atom [:plan-params])
        changed? (planning.cmn/params-changed? (rum/react params-atom) summary)
        ordered? (pos? ordered-count)]
    (when summary
      [:div.form-section
       (headings/heading "Plan Parameters" {:type :h3 :size :medium})
       [:div
        {:class (yuc/bem :flex-grid-group [:align-left :flush])}
        (map
          (fn [[label k]]
            [:div
             {:key (name k)
              :class (yuc/bem :flex-grid-block [:fluid])}
             [:div.flex-grid-content
              (forms/input label "" (rum/cursor params-atom k)
                {:type :number :disabled ordered?})]])
          [["Budget:" :budget]
           ["Weeks:" :period]
           ["Fillrate (%):" :fillrate]
           ["Min. Coverage (%):" :mincover]])]
       [:div.form-field
        [:span.form-hint (plan-cost-summary summary)]
        (when-let [note (plan-basis-note summary)]
          [:span.form-hint.u-block note])]
       [:div.form-field
        ;; :type :button matters — the panel sits inside the screen's form, and a
        ;; button with no type submits it, navigating away before the re-run replies.
        (buttons/button-web "Re-run Plan"
          {:size :default
           :type :button
           :disabled (or ordered? (not changed?))
           :on-click #(grid/dispatch-action app-atom :admin.planning-new/rerun-plan {})})
        (cond
          ordered?
          [:span.form-hint.u-margin-left-12
           "Orders have already been placed from this plan, so it can no longer be re-run."]

          (not changed?)
          [:span.form-hint.u-margin-left-12 "Change a parameter to re-run the plan."])]])))

(defn csv-export-filters
  "Download SKU CSV: by level 1 or 2 category, brand or supplier, each list
   narrowed to what the other choices leave."
  [suppliers]
  (let [supplier-names (into {} (map (juxt :id :companyname) suppliers))]
    [{:id :cat1 :label "Level 1 category" :value-fn (comp first :cats)}
     {:id :cat2 :label "Level 2 category" :value-fn (comp second :cats)}
     {:id :brandrootid :label "Brand" :value-fn :brandrootid :text-fn :brand}
     {:id :supplierid :label "Supplier" :value-fn :supplier_id :text-fn (comp supplier-names :supplier_id)}]))

(rum/defc plan-overview < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)


        plan-items-atom (rum/cursor-in process-atom [:items])
        plan-items (vals (rum/react plan-items-atom))

        top-level-cats
        (sort-by identity
          (keys (yuc/index-by (comp first :cats) plan-items)))

        pivot-by-suppliers-atom (rum/cursor-in process-atom [:pivot-by-suppliers])

        state-atom process-atom
        categories-atom (rum/cursor state-atom :categories)]
    [:div
     [:div
      {:class (yuc/bem :flex-grid-group [:flush :align-left :valign-middle])}
      [:div
       {:class (yuc/bem :flex-grid-block [:fluid :flush :grow-yes])}
       [:div.flex-grid-content
        (headings/heading "Plan Overview" {:type :h2 :size :large})]]
      [:div
       {:class (yuc/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Try new layout"
           (merge
             (w.cmn/try-new-layout-props)
             {:style :light
              :size :default}))]]
      [:div
       {:class (yuc/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Back to Planning"
          (merge
            (urlstate/href-props app-atom "planning-new")
            {:style :secondary
             :size :default}))]]]
     [:div
      [:form
       [:div.form-field
        (buttons/button-web "Order Overview"
          (merge
            (urlstate/href-props app-atom "planning-new" "order" {:planid (:planid (cmn/screen-params app-atom))})
            {:size :default}))]
       [:div.form-field
        (buttons/button-web "Download SKU CSV"
          {:size :default
           :style :secondary
           :type :button
           :on-click
           (fn [e]
             (.preventDefault e)
             (csvexport/open-export-modal app-atom
               {:title "Download SKU CSV"
                :text "Leave every filter blank to download the whole plan."
                :href (str "/api/planning/plan-csv?planid=" (:planid (cmn/screen-params app-atom)))
                :download-text "Download CSV"
                :count-text (fn [n] (str n " SKU" (when (not= 1 n) "s")))
                ;; qty = 0 rows are not in the CSV.
                :rows (filter #(pos? (or (:qty %) 0)) plan-items)
                :filters (csv-export-filters @(rum/cursor-in process-atom [:suppliers]))}))})]
       [:div.form-field
        (forms/toggle "Pivot by Suppliers" pivot-by-suppliers-atom
          {:change-fn
           #(planning.cmn/setup-overview-atom app-atom)})]
       (plan-parameters app-atom)

       (planner-category-matrix app-atom)

       (when-not (rum/react pivot-by-suppliers-atom)
         (into
           [:div {:class (yuc/bem :flex-grid-group [:align-left :flush :flush-force])}]
           (map
             (fn [category]
               [:div
                {:class (yuc/bem :flex-grid-block [:fluid])}
                [:div.flex-grid-content
                 [:div.form-field
                  (forms/checkbox
                    category
                    (rum/cursor categories-atom category)
                    {:change-fn #(planning.cmn/setup-overview-atom app-atom)})]]])
             top-level-cats)))]]

     (if (rum/react pivot-by-suppliers-atom)
       (datatables/table app-atom
         :stockorder.planning/supplier-overview
         plan-overview-table-fns)
       (datatables/table app-atom
         :admin.planning-new/category-overview
         plan-overview-table-fns))
     [:div.yc-hr]]))

(defmethod flow/render-screen :admin.planning-new/plan [app-atom _]
  (datatables/initiate app-atom supplier-overview-table-config {})
  (datatables/initiate app-atom category-overview-table-config {})
  (planning.cmn/with-loading-overlay app-atom (plan-overview app-atom)))

(defmethod t/transition-open :admin.planning-new/plan [app-atom _ params]
  (w.cmn/redirect-to-new-layout-if-needed! app-atom "planning" params))

(defmethod t/transition-after-open :admin.planning-new/plan [app-atom _]
  (grid/dispatch-action app-atom :stockorder.planning/plan
    {:planid (:planid (cmn/screen-params app-atom))}))

(defmethod grid/action :admin.planning-new/rerun-plan [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        summary @(rum/cursor-in process-atom [:plan-summary])
        {:keys [period fillrate mincover budget]} @(rum/cursor-in process-atom [:plan-params])]
    (planning.cmn/show-loading! app-atom "Re-running plan")
    ;; plantype and filter are required by the service but aren't the planner's
    ;; business here, so they go back exactly as the plan holds them.
    (comms/send :stockplan.planning/rerun
      {:action :edit
       :data
       [{:stockplan/planid (:planid summary)
         :stockplan/plantype (:plantype summary)
         :stockplan/filter (:filter summary)
         :stockplan/supplier (:supplier summary)
         ;; Omitted deliberately — the re-run panel doesn't edit scope, and
         ;; save-plan-categories! leaves it alone when the key isn't sent.

         :stockplan/period period
         :stockplan/fillrate fillrate
         :stockplan/mincover mincover
         :stockplan/budget budget
         :stockplan/effective-date (:effectivedate summary)}]})))

(defmethod comms/receive :stockplan.planning/rerun [app-atom e]
  (planning.cmn/hide-loading! app-atom)
  (cond
    (:error e)
    (toaster/open-toast app-atom :error (:error e) {:show-close-button true})

    :else
    (let [process-atom (cmn/process-cursor app-atom)]
      ;; The plan's items are what just changed, and validate-plan-items serves
      ;; them from cache while the planid still matches — drop them so it refetches.
      (swap! process-atom dissoc :items)
      (planning.cmn/validate-plan-items app-atom))))